// client/src/pages/secure/AdvertisingReviewDetailPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';

const AdvertisingReviewDetailPage = () => {
    const { id } = useParams();
    const [ad, setAd] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reviewStatus, setReviewStatus] = useState('');
    const [reviewNotes, setReviewNotes] = useState('');
    // The checklist is this office's state's rule text, served by the API.
    const [rules, setRules] = useState(null);
    const [checklist, setChecklist] = useState({});
    // Virtually staged / digitally altered photos add the state's labelling item.
    const [staged, setStaged] = useState(false);
    const [teamName, setTeamName] = useState('');
    const [teamResult, setTeamResult] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const apiClient = useApiClient();

    const fetchAdDetails = useCallback(async () => {
        try {
            const [data, r] = await Promise.all([apiClient.get(`/api/advertising/${id}`), apiClient.get('/api/advertising/rules')]);
            setAd(data);
            setRules(r);
            setReviewStatus(data.status);
            setReviewNotes(data.notes || '');
            let saved = data.review_checklist || {};
            if (typeof saved === 'string') { try { saved = JSON.parse(saved); } catch { saved = {}; } }
            setStaged(!!Number(data.contains_virtual_staging));
            setChecklist(Object.fromEntries([...r.checklist, ...(r.conditional || [])].map((i) => [i.key, !!saved[i.key]])));
        } catch (err) {
            setError(err.message || 'Failed to fetch ad data.');
        } finally {
            setIsLoading(false);
        }
    }, [id, apiClient]);

    useEffect(() => {
        fetchAdDetails();
    }, [fetchAdDetails]);

    const handleChecklistChange = (e) => {
        const { name, checked } = e.target;
        setChecklist(prev => ({ ...prev, [name]: checked }));
    };

    const activeItems = rules ? [...rules.checklist, ...(staged ? (rules.conditional || []) : [])] : [];
    const isApproveDisabled = reviewStatus === 'Approved' && (!rules || activeItems.some((i) => !checklist[i.key]));

    const checkTeam = async () => {
        try { setTeamResult(await apiClient.post('/api/advertising/check-team-name', { name: teamName })); }
        catch (err) { setTeamResult({ ok: false, problem: err.message }); }
    };

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError('');
        try {
            const payload = {
                status: reviewStatus,
                notes: reviewNotes,
                checklist,
                contains_virtual_staging: staged,
            };
            const updatedAd = await apiClient.put(`/api/advertising/${id}/review`, payload);
            setAd(updatedAd);
            // Optionally show a success message
        } catch (err) {
            setError(err.message || 'Failed to save review.');
        } finally {
            setIsSaving(false);
        }
    };
    
    if (isLoading) return <div className="p-8">Loading ad details...</div>;

    return (
        <div>
            <div className="mb-6"><Link to="/dashboard/advertising">&larr; Back to Ad Review List</Link></div>
            {error && <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg">{error}</div>}
            {ad && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-8">
                        {/* Ad details and preview */}
                        <div className="bg-white p-8 rounded-lg shadow">
                            <h1 className="text-3xl font-bold">Ad Review: {ad.ad_title || `Ad #${ad.ad_id}`}</h1>
                            {/* Ad preview would go here */}
                        </div>
                    </div>

                    <div className="bg-white p-8 rounded-lg shadow">
                        <h2 className="text-2xl font-bold text-slate-800 mb-6">Broker Review</h2>
                        <form onSubmit={handleReviewSubmit} className="space-y-6">
                            {/* Compliance Checklist */}
                            <div className="space-y-3 p-4 border rounded-md">
                                <h3 className="font-semibold text-slate-700">{rules ? `${rules.state} advertising checklist` : 'Compliance checklist'}</h3>
                                <label className="flex items-start text-sm font-medium"><input type="checkbox" checked={staged} onChange={(e) => setStaged(e.target.checked)} className="mr-2 mt-1"/>
                                    <span>This ad contains virtually staged or digitally altered photos</span></label>
                                {activeItems.map((i) => (
                                    <label key={i.key} className="flex items-start text-sm"><input type="checkbox" name={i.key} checked={!!checklist[i.key]} onChange={handleChecklistChange} className="mr-2 mt-1"/>
                                        <span>{i.text} <span className="text-xs text-slate-500">({i.cite})</span></span></label>
                                ))}
                                <div className="pt-2 border-t">
                                    <p className="text-xs text-slate-600 mb-1">Check a team name against the rule ({rules?.team?.cite}):</p>
                                    <div className="flex gap-2"><input value={teamName} onChange={(e) => { setTeamName(e.target.value); setTeamResult(null); }} placeholder="e.g. The Smith Team" className="flex-1 p-1.5 border rounded text-sm" />
                                        <button type="button" onClick={checkTeam} className="px-2 py-1 text-sm bg-slate-200 rounded">Check</button></div>
                                    {teamResult && <p className={`text-xs mt-1 ${teamResult.ok ? 'text-emerald-700' : 'text-red-700'}`}>{teamResult.ok ? 'Allowed.' : teamResult.problem}</p>}
                                </div>
                                {(rules?.reminders || []).map((r) => <p key={r} className="text-xs text-slate-500">• {r}</p>)}
                            </div>

                            {/* Review Action */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Review Action</label>
                                <select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)} className="w-full p-2 border rounded">
                                    <option>Pending</option><option>Approved</option><option>Rejected</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium">Review Notes</label>
                                <textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} rows="6" className="mt-1 block w-full p-2 border rounded"/>
                            </div>
                            
                            <div className="pt-4">
                                <button type="submit" disabled={isSaving || isApproveDisabled} className="w-full px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                                    {isSaving ? 'Saving...' : 'Submit Review'}
                                </button>
                                {isApproveDisabled && <p className="text-xs text-red-600 text-center mt-2">All checklist items must be checked to approve.</p>}
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdvertisingReviewDetailPage;